import { accountsApiRtkQuery } from '@chillibean/accounts-api-client-sdk';
import { Spinner, titleizeString } from '@chillibean/component-library';
import { Table, Dropdown, Pill, Title, BackButton, ScrollView } from '@chillibean/component-library/legacy';
import pluralize from 'pluralize';
import { useState, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import LogAndDisplayError from '../../../LogAndDisplayError/LogAndDisplayError';
import useNavigator from '../hooks/useNavigator';
import ExportModal from '../presentational/ExportModal';
import { selectInternalResult } from '../redux/internal.slice';
import prepareAssetRowData from '../utils/protocol/generalHelpers';

function SiteReportTemplateReport() {
  // state
  const [tableHeaders, setTableHeaders] = useState([]);
  const [tableDataRows, setTableDataRows] = useState([]);
  const [tableDataReady, setTableDataReady] = useState(false);
  const [tableEmpty, setTableEmpty] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [protocolName, setProtocolName] = useState(null);
  const [studyProcedureDisplayName, setStudyProcedureDisplayName] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // set variables
  const { protocolId } = useParams();
  const { useGetProtocolsReportingQuery } = accountsApiRtkQuery;

  const { accountId } = useSelector(selectInternalResult);
  const { data, error, isLoading, refetch } = useGetProtocolsReportingQuery(
    { accountId: accountId, studyProtocolId: protocolId, page: currentPage },
    { skip: !accountId },
  );

  // handlers
  const { goBack } = useNavigator();

  const toggleExportModal = () => setExportModalVisible(!exportModalVisible);

  const tableHasMoreRecords = () => {
    if (data && tableDataReady) {
      const { hits, perPage } = data;

      return hits > perPage && tableDataRows.length < hits;
    }

    return false;
  };

  const fetchData = () => {
    const nextPage = currentPage + 1;
    refetch({ page: nextPage });
    setCurrentPage(nextPage);
  };

  useEffect(() => {
    if (!accountId || currentPage !== 1) return;

    if (!isLoading && data) {
      setProtocolName(data?.studyProtocol?.name);
      setStudyProcedureDisplayName(data?.studyProtocol?.studyProcedureDisplayName);
      if (data?.results.length === 0) {
        setTableEmpty(true);
      } else {
        const preparedTableData = prepareAssetRowData(data?.results);

        setTableHeaders([...preparedTableData.headersReadyForTable]);
        setTableDataRows([...preparedTableData.dataReadyForTable]);
        setTableDataReady(true);
        setTableEmpty(false);
      }
    }
  }, [accountId, isLoading]);

  useEffect(() => {
    if (!isLoading && data && data?.results.length !== 0) {
      const processedData = prepareAssetRowData(data?.results).dataReadyForTable;
      setTableDataRows(tableDataRows.concat(processedData));
    }
  }, [currentPage]);

  if (error) {
    return <LogAndDisplayError error={error} />;
  }

  const tablePlaceholder = (
    <EmptyTableContainer>
      <InfoEmptyContainer>
        <ClearFilterText>There are no procedures to show yet.</ClearFilterText>
      </InfoEmptyContainer>
    </EmptyTableContainer>
  );

  return (
    <div className="single_column_container">
      <ExportModal
        showModal={exportModalVisible}
        setShowModal={setExportModalVisible}
        protocolID={protocolId}
        exportEndpoint="SITES"
      />
      <div className="header-wrapper-formReport">
        <div className="left-items">
          <BackButton onClickCallback={goBack} loading={isLoading} />
          <Title
            heading={`${protocolName}: ${titleizeString(
              studyProcedureDisplayName?.singular || 'Study Procedure',
            )} Report`}
            subheading=""
            loading={isLoading || !protocolName || !studyProcedureDisplayName}
          />
          {tableEmpty === false && (
            <Pill
              dataCount={String(data?.hits) || '0'}
              dataContext={pluralize(
                titleizeString(studyProcedureDisplayName?.singular || 'Study Procedure'),
                data?.hits,
              )}
              loading={isLoading || !data}
            />
          )}
        </div>
        <div className="right-items">
          {!isLoading && (
            <Dropdown
              options={[
                {
                  label: 'Export data',
                  onSelect: toggleExportModal,
                },
              ]}
              icon="kebab"
            />
          )}
        </div>
      </div>
      <div className="scroll-table-wrapper">
        {tableEmpty ? (
          tablePlaceholder()
        ) : (
          <InfiniteScroll
            dataLength={tableDataRows.length}
            next={fetchData}
            scrollableTarget="scrollableDiv"
            hasMore={tableHasMoreRecords()}
            style={{ height: '100%' }}
          >
            <ScrollView idName="scrollableDiv">
              <Table
                headers={tableHeaders}
                tableData={tableDataRows}
                loading={!tableDataReady}
                hasMore={tableHasMoreRecords()}
                spinnerComponent={<Spinner />}
              />
            </ScrollView>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
}

const EmptyTableContainer = styled.div`
  background-color: #f4f7fb;
  width: 100%;
  height: 100%;
  text-align: center;
  font-weight: 400;
  font-size: 13px;
  justify-content: center;
`;

EmptyTableContainer.displayName = 'EmptyTableContainer';

const ClearFilterText = styled.div`
  padding-bottom: 8px;
`;

const InfoEmptyContainer = styled.div`
  padding-top: 40vh;
`;

export default SiteReportTemplateReport;
